import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Checkbox, InlineNotification, SearchField, Table, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { useConsentTemplateSiteAssignments } from '../../hooks/use_consent_template_site_assignments.hook';
import { EnvironmentSelector } from '../../site_configuration/environment_selector.component';
export const isPublishedSiteAssignmentWritable = (status, canEditTemplate) => status === 'Published' && canEditTemplate;
const matchesSiteSearch = (site, query) => {
    if (!query) {
        return true;
    }
    const name = (site.name ?? '').toLowerCase();
    const number = (site.number ?? '').toLowerCase();
    const piName = (site.piName ?? '').toLowerCase();
    return name.includes(query) || number.includes(query) || piName.includes(query);
};
const siteAssignmentColumns = (translate, setSiteAssigned, interactive) => [
    ...(interactive
        ? [
            {
                id: 'assigned',
                size: 48,
                cell: (context) => (_jsx(Checkbox, { label: translate('Assign site'), hideLabel: true, checked: context.row.original.assigned, onChange: (checked) => setSiteAssigned(context.row.original.id, checked), dataTestId: `configuration-sites-edit-checkbox-${context.row.original.id}` })),
            },
        ]
        : []),
    {
        header: translate('Site Number'),
        accessorKey: 'number',
        cell: (context) => context.getValue() ?? '—',
    },
    {
        header: translate('Site Name'),
        accessorKey: 'name',
        cell: (context) => context.getValue() ?? '—',
    },
    {
        header: translate('PI Name'),
        accessorKey: 'piName',
        cell: (context) => context.getValue() ?? '—',
    },
    {
        header: translate('Site Address'),
        accessorKey: 'addressDisplay',
        cell: (context) => context.getValue() ?? '—',
    },
];
const SitesAssignmentTable = ({ rows, columns, isLoading, searchValue, onSearchChange, environments, selectedEnvironmentId, onSelectedEnvironmentIdChange, searchTestId, tableTestId, }) => {
    const translate = useLocalizeMessage();
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'flex flex-wrap items-end gap-4', children: [_jsx(SearchField, { ariaLabel: translate('Search'), placeholder: translate('Search site number, name or PI'), value: searchValue, onChange: (event) => onSearchChange(event.target.value), onClear: () => onSearchChange(''), width: 'base', dataTestId: searchTestId }), _jsx(EnvironmentSelector, { environments: environments, selectedEnvironmentId: selectedEnvironmentId, onChange: onSelectedEnvironmentIdChange, selectTestId: `${tableTestId}-environment` })] }), _jsx("div", { "data-testid": tableTestId, children: _jsx(Table, { columns: columns, data: rows, getRowId: (row) => String(row.id), isLoading: isLoading, labelNoResults: translate('No sites') }) })] }));
};
export const ConfigurationSitesSection = ({ enabled, templateKey, status, canEditTemplate, entityId, studyId, environments, sites, onAssignmentsSaved, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const [viewSearchValue, setViewSearchValue] = useState('');
    const [editSearchValue, setEditSearchValue] = useState('');
    const [isEditOpen, setIsEditOpen] = useState(false);
    const panelRef = useRef(null);
    const writable = isPublishedSiteAssignmentWritable(status, canEditTemplate);
    const { selectedEnvironmentId, setSelectedEnvironmentId, environmentSites, selectedSiteIds, setSiteAssigned, restoreSavedSelection, loadState, isDirty, isSaving, save, } = useConsentTemplateSiteAssignments({
        enabled,
        templateKey,
        entityId,
        studyId,
        sites,
    });
    const assignedIds = useMemo(() => new Set(selectedSiteIds), [selectedSiteIds]);
    const mappedRows = useMemo(() => environmentSites.map((site) => ({ ...site, assigned: assignedIds.has(site.id) })), [assignedIds, environmentSites]);
    const assignedRows = useMemo(() => {
        const query = viewSearchValue.trim().toLowerCase();
        return mappedRows.filter((row) => row.assigned && matchesSiteSearch(row, query));
    }, [mappedRows, viewSearchValue]);
    const editRows = useMemo(() => {
        const query = editSearchValue.trim().toLowerCase();
        return mappedRows.filter((row) => matchesSiteSearch(row, query));
    }, [editSearchValue, mappedRows]);
    const viewColumns = useMemo(() => siteAssignmentColumns(translate, setSiteAssigned, false), [setSiteAssigned, translate]);
    const editColumns = useMemo(() => siteAssignmentColumns(translate, setSiteAssigned, true), [setSiteAssigned, translate]);
    const closeEdit = () => {
        restoreSavedSelection();
        setIsEditOpen(false);
    };
    useEffect(() => {
        if (!isEditOpen) {
            return;
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        panelRef.current?.focus();
        const onKeyDown = (event) => {
            if (event.key !== 'Escape' || isSaving) {
                return;
            }
            if (document.querySelector('[data-presentation="popover"]')) {
                return;
            }
            restoreSavedSelection();
            setIsEditOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isEditOpen, isSaving, restoreSavedSelection]);
    const handleSave = async () => {
        const errorMessage = await save();
        if (!errorMessage) {
            addNotification({
                type: 'success',
                title: translate('Site assignments saved'),
            });
            onAssignmentsSaved?.();
            setIsEditOpen(false);
            return;
        }
        restoreSavedSelection();
        addNotification({
            type: 'error',
            title: translate('Site assignments could not be saved'),
            subtitle: errorMessage,
        });
    };
    const openEdit = () => {
        setEditSearchValue('');
        setIsEditOpen(true);
    };
    const handleEnvironmentIdChange = (environmentId) => {
        if (environmentId === selectedEnvironmentId) {
            return;
        }
        if (isDirty) {
            restoreSavedSelection();
        }
        setSelectedEnvironmentId(environmentId);
    };
    const tableProps = {
        isLoading: loadState.status === 'loading',
        environments,
        selectedEnvironmentId,
        onSelectedEnvironmentIdChange: handleEnvironmentIdChange,
    };
    return (_jsxs("section", { className: 'nucleus-flex nucleus-flex-col nucleus-gap-4', "data-testid": 'configuration-sites-section', children: [_jsxs("div", { className: 'nucleus-flex nucleus-items-center nucleus-justify-between', children: [_jsx("h3", { className: 'nucleus-m-0 nucleus-text-base nucleus-font-semibold nucleus-text-neutral-900', children: translate('Sites') }), writable && (_jsx("button", { type: 'button', className: 'border-0 bg-transparent p-0 text-[13.5px] font-medium leading-[18px] text-primary-500', onClick: openEdit, "data-testid": 'configuration-sites-edit', children: translate('Edit') }))] }), !writable && (_jsx("p", { className: 'nucleus-text-sm nucleus-text-neutral-600', "data-testid": 'configuration-sites-locked', children: translate('Site assignment can be changed only when this template is Published.') })), _jsx(SitesAssignmentTable, { ...tableProps, rows: assignedRows, columns: viewColumns, searchValue: viewSearchValue, onSearchChange: setViewSearchValue, searchTestId: 'configuration-sites-search', tableTestId: 'configuration-sites-table' }), loadState.status === 'error' && (_jsx(InlineNotification, { type: 'error', title: translate('Site assignments could not be loaded'), subtitle: loadState.message })), isEditOpen
                ? createPortal(
                // The environment menu is portaled to document.body at z-index 50, so this layer stays under it.
                _jsxs("div", { className: 'fixed inset-0 z-[40]', "data-testid": 'configuration-sites-edit-layer', children: [_jsx("div", { className: 'absolute inset-0 bg-black/60', "aria-hidden": true }), _jsx("div", { className: 'relative flex h-full items-center justify-center p-6', children: _jsxs("div", { ref: panelRef, role: 'dialog', "aria-modal": 'true', "aria-labelledby": 'configuration-sites-edit-title', tabIndex: -1, className: 'flex max-h-[min(80vh,720px)] w-full max-w-[976px] flex-col overflow-hidden rounded-xl bg-white shadow-md outline-none', "data-testid": 'configuration-sites-edit-modal', children: [_jsx("header", { className: 'flex shrink-0 items-center border-0 border-b border-solid border-neutral-200 px-6 pb-3 pt-4', children: _jsx("h2", { id: 'configuration-sites-edit-title', className: 'm-0 text-2xl font-semibold text-neutral-900', children: translate('Sites') }) }), _jsx("div", { className: 'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6', children: _jsx(SitesAssignmentTable, { ...tableProps, rows: editRows, columns: editColumns, searchValue: editSearchValue, onSearchChange: setEditSearchValue, searchTestId: 'configuration-sites-edit-search', tableTestId: 'configuration-sites-edit-table' }) }), _jsxs("footer", { className: 'flex shrink-0 justify-end gap-3 border-0 border-t border-solid border-neutral-200 p-4', children: [_jsx(Button, { variant: 'secondary', label: translate('Cancel'), onClick: closeEdit, disabled: isSaving, dataTestId: 'configuration-sites-cancel' }), _jsx(Button, { label: translate('Save'), onClick: () => void handleSave(), disabled: !isDirty || isSaving || loadState.status !== 'ready', dataTestId: 'configuration-sites-save' })] })] }) })] }), document.body)
                : null] }));
};
